import {
  familyOf,
  isAssessmentStatusUnderWay,
  parseInstrumentName,
  summaryOf,
  type Assessment,
  type AssessmentSummaryView,
  type CurrentUser,
  type Person,
  type PersonInvolvement,
} from "@thalamos/haven-common";

import type { FormLoading } from "./AssessmentContext.js";
import { latestSubmission } from "./latestSubmission.js";
import {
  factsOf,
  momentOf,
  type SummarisedAssessment,
  type SummaryFacts,
} from "./summaryFacts.js";

/** What the page shows where the summary itself is: the summary, or why there is none. */
export type SummaryBody =
  | { kind: "loading" }
  | { kind: "unavailable" }
  | { kind: "withheld" }
  | { kind: "not_submitted" }
  | { kind: "uncategorised" }
  | { kind: "summarised"; summary: AssessmentSummaryView };

/** Why the assessment asked to compare with is not compared with, which leaves this one shown alone. */
export type ComparisonProblem =
  | "unreadable"
  | "other_person"
  | "other_instrument"
  | "same_assessment"
  | "not_submitted"
  | "not_finished"
  | "not_earlier";

/** The assessment to compare with, as far as reading it has got. */
export type ComparedReading =
  | { kind: "loading" }
  | { kind: "unreadable" }
  | { kind: "read"; assessment: Assessment; form: FormLoading };

export type SummaryState = {
  body: SummaryBody;
  /**
   * Absent where there is no submission to describe or its answers are
   * withheld, and while a comparison asked for is still being judged.
   */
  facts?: SummaryFacts | undefined;
  /** Absent unless an assessment to compare with was asked for and could not be. */
  problem?: ComparisonProblem | undefined;
};

const familyOfName = (name: string): string | undefined => {
  const parsed = parseInstrumentName(name);

  return parsed.success ? familyOf(parsed.data) : undefined;
};

/** The assessment with the submission a summary shows of it: its latest, approved where it was. */
const summarisable = (
  assessment: Assessment,
): SummarisedAssessment | undefined => {
  const submission = latestSubmission(assessment);

  return submission === undefined ? undefined : { assessment, submission };
};

/**
 * Why `compared` cannot be compared with `current`, or nothing where it can.
 *
 * The address can name any assessment, so it is held to what the list offers:
 * an earlier approved or completed assessment of the same young person on an
 * instrument of the same family. Another young person's is refused even where
 * it can be read, since a comparison sets one child's answers against
 * another's, and the same family because an item key means one thing only
 * within its family.
 */
const problemWith = (
  current: SummarisedAssessment,
  compared: Assessment,
  person: Person,
): ComparisonProblem | undefined => {
  const records = [
    person.id,
    ...person.mergedPersonRecords.map(({ id }) => id),
  ];

  if (compared.answersWithheld === true) {
    return "unreadable";
  }

  if (!records.includes(compared.personId)) {
    return "other_person";
  }

  const family = familyOfName(current.assessment.instrument.name);

  if (
    family === undefined ||
    familyOfName(compared.instrument.name) !== family
  ) {
    return "other_instrument";
  }

  if (compared.id === current.assessment.id) {
    return "same_assessment";
  }

  const reading = summarisable(compared);

  if (reading === undefined) {
    return "not_submitted";
  }

  if (isAssessmentStatusUnderWay(compared.status)) {
    return "not_finished";
  }

  return momentOf(reading).at < momentOf(current).at
    ? undefined
    : "not_earlier";
};

/** What judging the assessment asked to compare with came to. */
type Judged =
  | { kind: "waiting" }
  | {
      kind: "judged";
      problem?: ComparisonProblem | undefined;
      beside?: { reading: SummarisedAssessment; form: FormLoading } | undefined;
    };

const judge = (
  current: SummarisedAssessment,
  compared: ComparedReading | undefined,
  person: Person | undefined,
): Judged => {
  if (compared === undefined) {
    return { kind: "judged" };
  }

  if (compared.kind === "loading") {
    return { kind: "waiting" };
  }

  if (compared.kind === "unreadable") {
    return { kind: "judged", problem: "unreadable" };
  }

  // Whether it is about the same young person waits for their record.
  if (person === undefined) {
    return { kind: "waiting" };
  }

  const problem = problemWith(current, compared.assessment, person);
  const reading = summarisable(compared.assessment);

  return problem === undefined && reading !== undefined
    ? { kind: "judged", beside: { reading, form: compared.form } }
    : { kind: "judged", problem };
};

/**
 * What the summary page shows of an assessment, alone or compared with
 * another: the summary waits for both forms, and a comparison that cannot be
 * made is said so, with this assessment shown alone.
 */
export const summaryStateOf = ({
  current,
  currentForm,
  compared,
  person,
  involvements,
  viewer,
}: {
  current: Assessment;
  currentForm: FormLoading;
  /** Absent where no comparison was asked for. */
  compared?: ComparedReading | undefined;
  /** The young person the assessment is about; undefined until read. */
  person: Person | undefined;
  involvements: PersonInvolvement[] | undefined;
  viewer: CurrentUser | undefined;
}): SummaryState => {
  if (current.answersWithheld === true) {
    return { body: { kind: "withheld" } };
  }

  const shown = summarisable(current);

  if (shown === undefined) {
    return { body: { kind: "not_submitted" } };
  }

  const judged = judge(shown, compared, person);

  if (judged.kind === "waiting") {
    return { body: { kind: "loading" } };
  }

  const { problem, beside } = judged;
  const facts = factsOf({
    current: shown,
    previous: beside?.reading,
    involvements,
    viewer,
    schema:
      currentForm.kind === "ready" ? currentForm.loaded.schema : undefined,
  });
  const besideForm = beside?.form;

  if (currentForm.kind === "failed" || besideForm?.kind === "failed") {
    return { body: { kind: "unavailable" }, facts, problem };
  }

  if (currentForm.kind === "loading" || besideForm?.kind === "loading") {
    return { body: { kind: "loading" }, facts, problem };
  }

  const result = summaryOf(
    {
      schema: currentForm.loaded.schema,
      form: currentForm.loaded.form,
      responses: shown.submission.responses,
    },
    beside === undefined || besideForm === undefined
      ? undefined
      : {
          schema: besideForm.loaded.schema,
          form: besideForm.loaded.form,
          responses: beside.reading.submission.responses,
        },
  );

  return {
    body:
      result.kind === "summarised"
        ? { kind: "summarised", summary: result.summary }
        : { kind: "uncategorised" },
    facts,
    problem,
  };
};
